import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useRef, useState } from 'react';

import { noop } from 'lib/utils';

import { useCommentsRoot } from '../../../Root';
import Item from './Item';

const TextEditor = dynamic(() => import('../../../TextEditor'), {
  ssr: false,
});

const TIME_TO_FINISH_ANIMATIONS_OF_OPENED_EDITOR = 300;

const CommentItem = forwardRef(
  (
    {
      canBeRecommendedAsReview,
      children,
      commentDate,
      commentFixed = false,
      commentId,
      commentLikes,
      commentStatusName,
      commentText,
      hasReply,
      isDisliked = false,
      isEdited,
      isLiked = false,
      isReply = false,
      isReview = false,
      isShowingReplies = false,
      parentId = null,
      userId,
      userLevel,
      userName,
      userPhoto,
      userSpecialtyName = null,
      userTypeName,
      userUsername,
      onToggleReplies = noop,
    },
    ref
  ) => {
    const [isShowingTextEditor, setShowingTextEditor] = useState(false);
    const prevActiveAnswerCommentId = useRef(null);
    const { activeAnswerCommentId } = useCommentsRoot();

    useEffect(() => {
      if (activeAnswerCommentId !== commentId) {
        prevActiveAnswerCommentId.current = activeAnswerCommentId;
        setShowingTextEditor(false);
        return;
      }

      if (!prevActiveAnswerCommentId.current) {
        setShowingTextEditor(true);
      }

      const timeout = setTimeout(() => {
        setShowingTextEditor(true);
      }, TIME_TO_FINISH_ANIMATIONS_OF_OPENED_EDITOR);

      prevActiveAnswerCommentId.current = activeAnswerCommentId;

      return () => {
        clearTimeout(timeout);
      };
    }, [activeAnswerCommentId]);

    return (
      <Item
        ref={ref}
        canBeRecommendedAsReview={canBeRecommendedAsReview}
        commentDate={commentDate}
        commentFixed={commentFixed}
        commentId={commentId}
        commentLikes={commentLikes}
        commentStatusName={commentStatusName}
        commentText={commentText}
        hasReply={hasReply}
        isDisliked={isDisliked}
        isEdited={isEdited}
        isLiked={isLiked}
        isReply={isReply}
        isReview={isReview}
        isShowingReplies={isShowingReplies}
        userId={userId}
        userLevel={userLevel}
        userName={userName}
        userPhoto={userPhoto}
        userSpecialtyName={userSpecialtyName}
        userTypeName={userTypeName}
        userUsername={userUsername}
        onToggleReplies={onToggleReplies}
      >
        {isShowingTextEditor && (
          <TextEditor
            answering={userUsername}
            parentId={parentId || commentId}
          />
        )}
        {children}
      </Item>
    );
  }
);

CommentItem.displayName = 'CommentItem';

CommentItem.propTypes = {
  canBeRecommendedAsReview: PropTypes.bool,
  children: PropTypes.node,
  commentDate: PropTypes.string.isRequired,
  commentFixed: PropTypes.bool,
  commentId: PropTypes.number.isRequired,
  commentLikes: PropTypes.number,
  commentStatusName: PropTypes.string.isRequired,
  /**
   * Should be required but there are edge cases where it could be null.
   * There were bugs where users could send an empty comment
   */
  commentText: PropTypes.string,
  isDisliked: PropTypes.bool,
  isEdited: PropTypes.bool.isRequired,
  isLiked: PropTypes.bool,
  isReply: PropTypes.bool,
  isReview: PropTypes.bool,
  isShowingReplies: PropTypes.bool,
  parentId: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userLevel: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userTypeName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onToggleReplies: PropTypes.func,
};

export default memo(CommentItem);
